import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import base from '@/_api/base';
import { validateNewPassword } from '@/lib/password-validation';
import { usePasswordChangeMutation } from '@/queries/auth/usePasswordChangeMutation';
import { useAuthStore } from '@/stores/auth-store';
import PasswordField from './PasswordField';
import PasswordRequirements from './PasswordRequirements';

export default function ChangePasswordForm({ onCancel, onSuccess }) {
  const accountName = useAuthStore((s) => s.currentUser?.accountName);
  const changePassword = usePasswordChangeMutation();
  const [verifyError, setVerifyError] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (values) => {
    setVerifyError(null);
    try {
      await base.post('/admin/login', {}, {
        accountName,
        password: values.currentPassword,
      });
    } catch {
      setVerifyError('Current password is incorrect');
      return;
    }

    try {
      await changePassword.mutateAsync(values.newPassword);
      onSuccess?.();
    } catch {
      /* notification from mutation */
    }
  };

  const busy = isSubmitting || changePassword.isPending;

  return (
    <form className="admin-form text-left" onSubmit={handleSubmit(onSubmit)}>
      <PasswordField
        id="change-current"
        label="Current Password"
        placeholder="••••••••"
        autoComplete="current-password"
        register={register('currentPassword', { required: 'Required' })}
        error={verifyError || errors.currentPassword?.message}
      />
      <PasswordField
        id="change-new"
        label="New Password"
        placeholder="Min. 8 characters"
        autoComplete="new-password"
        register={register('newPassword', {
          validate: validateNewPassword,
        })}
        error={errors.newPassword?.message}
        className="mt-4"
      />
      <PasswordRequirements password={newPassword} />
      <PasswordField
        id="change-confirm"
        label="Confirm New Password"
        placeholder="Repeat new password"
        autoComplete="new-password"
        register={register('confirmPassword', {
          required: 'Required',
          validate: (v, formValues) =>
            v === formValues.newPassword || 'Passwords do not match',
        })}
        error={errors.confirmPassword?.message}
        className="mt-4"
      />
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] px-6 font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] disabled:opacity-60"
        >
          <FontAwesomeIcon icon={['fas', 'floppy-disk']} />
          Save
        </button>
        <button
          type="button"
          disabled={busy}
          className="inline-flex min-h-11 flex-1 items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-[var(--bethere-primary)] bg-transparent px-6 font-bold text-[var(--bethere-primary)] disabled:opacity-60"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
